import React from 'react';
import { useLazyQuery } from '@apollo/client';
import { basePaths } from 'app-constants';
import { TrackedLink } from 'components/common/TrackedLink';
import { useWorkspacePath } from 'pages/workspaces/useWorkspace';
import { GetSecondarySourcesDocumentPartsDocument } from '__generated__/graphql';
import { useQueryAndHash } from './useQueryAndHash';
import { LinkHelperProps } from './index';

export function localRuleRefGlobalMatcher(refglobalString: string) {
  const localCourtRegex = /rule:\/ca_([a-z]*)\/ruleofcourt\/(.*)/;
  return refglobalString.match(localCourtRegex);
}

function modifyLocalRulesOfCourtRefGlobal(refglobalString: string) {
  const localRulesMatch = localRuleRefGlobalMatcher(refglobalString);
  if (localRulesMatch) {
    return `/ca_${localRulesMatch[1]}/ruleofcourt/${localRulesMatch[2].replace(/_[a-zA-Z0-9]/g, '')}`;
  }
  return '';
}

type CourtRuleType = 'ca' | 'local';

interface CourtRuleLinkProps extends LinkHelperProps {
  type?: CourtRuleType;
  id?: string;
  documentPartId?: string;
  refglobal?: string;
}

export type { CourtRuleLinkProps };

export function parseCourtRulePath(path: string) {
  // Parse court rule paths like /primary-law/rules/ca/{id} or /primary-law/rules/local/{id}/{documentPartId}
  const caRulePathRegex = new RegExp(`^${basePaths.courtRules}/ca/([^/]+)$`);
  const localRulePathRegex = new RegExp(
    `^${basePaths.courtRules}/local/([^/]+)/([^/]+)$`,
  );

  const caMatch = path.match(caRulePathRegex);
  if (caMatch) {
    return {
      type: 'ca' as const,
      id: caMatch[1],
    };
  }

  const localMatch = path.match(localRulePathRegex);
  if (localMatch) {
    return {
      type: 'local' as const,
      id: localMatch[1],
      documentPartId: localMatch[2],
    };
  }

  return null;
}

export function useCourtRulePath(
  type: CourtRuleType = 'ca',
  id?: string,
  documentPartId?: string,
  refglobal?: string,
  query?: string | null,
  hash?: string | null,
) {
  const [getDocumentParts, { loading, data }] = useLazyQuery(
    GetSecondarySourcesDocumentPartsDocument,
  );
  const workspacePath = useWorkspacePath();

  React.useEffect(() => {
    if (type === 'local' && !documentPartId && refglobal) {
      const modifiedRefGlobal = modifyLocalRulesOfCourtRefGlobal(refglobal);
      getDocumentParts({ variables: { refglobal: modifiedRefGlobal } });
    }
  }, [type, documentPartId, getDocumentParts, refglobal]);

  const path = React.useMemo(() => {
    const parts = [basePaths.courtRules];

    if (type === 'local') {
      if (documentPartId && id) {
        parts.push('local', id, documentPartId);
      } else if (!loading && data) {
        // do lazy query to get id and documentPartId
        parts.push(
          'local',
          data.secondarySourcesDocumentParts[0].documentId,
          data.secondarySourcesDocumentParts[0].documentPartId,
        );
        const basePath = parts.filter(Boolean).join('/');
        return workspacePath + basePath;
      } else {
        return '#';
      }
    } else {
      return getCaliforniaCourtRulesPath(id, workspacePath);
    }
  }, [type, id, documentPartId, workspacePath, loading, data]);

  return useQueryAndHash(path as string, query, hash);
}

export function getCaliforniaCourtRulesPath(
  id: string,
  workspacePath: string = '',
) {
  const parts = [basePaths.courtRules];
  if (id) parts.push('ca', id || '');
  const basePath = parts.filter(Boolean).join('/');
  return workspacePath + basePath;
}

export function CourtRuleLink({
  to,
  type = 'ca',
  id,
  documentPartId,
  refglobal,
  query,
  hash,
  ...props
}: CourtRuleLinkProps) {
  const path = useCourtRulePath(
    type,
    id,
    documentPartId,
    refglobal,
    query,
    hash,
  );
  return <TrackedLink to={to ? to : path} {...props} />;
}
